import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Palette,
  Check,
  X,
  Upload,
  RefreshCw,
  Building2,
  ChevronDown,
  ChevronRight,
  Download,
  AlertCircle,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common colour hex values for quick selection
const COMMON_COLOURS = {
  "Black": "#000000",
  "White": "#FFFFFF",
  "Navy": "#1B2A4E",
  "Grey Marle": "#9CA3AF",
  "Charcoal": "#374151",
  "Coal": "#1F2937",
  "Army": "#4B5320",
  "Bone": "#E8E0D5",
  "Natural": "#F5F5DC",
  "Red": "#DC2626",
  "Royal": "#1D4ED8",
  "Petrol Blue": "#1E3A5F",
  "Forest": "#228B22",
  "Sand": "#C2B280",
  "Khaki": "#C3B091",
};

export default function SupplierColours() {
  const { isAdmin } = useAuth();
  const [colours, setColours] = useState([]);
  const [bySupplier, setBySupplier] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("all");
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingColour, setEditingColour] = useState(null);
  const [formData, setFormData] = useState({ supplier: "", name: "", hex: "" });
  const [isSaving, setIsSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  
  // Expanded suppliers
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  
  // CSV Import state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importType, setImportType] = useState("colours"); // "colours" or "product-colours"
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    loadColours();
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSupplier, showInactive]);

  const loadColours = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSupplier && filterSupplier !== "all") {
        params.append("supplier", filterSupplier);
      }
      if (!showInactive) params.append("active_only", "true");
      else params.append("active_only", "false");
      
      const res = await axios.get(`${API}/supplier-colours?${params}`);
      const coloursData = res.data?.colours || [];
      const bySupplierData = res.data?.by_supplier || {};
      
      setColours(coloursData);
      setBySupplier(bySupplierData);
      
      // Auto-expand all suppliers
      const expanded = {};
      Object.keys(bySupplierData).forEach(s => expanded[s] = true);
      setExpandedSuppliers(expanded);
    } catch (error) {
      console.error("Failed to load colours", error);
      toast.error("Failed to load colours");
      // Set empty state on error to prevent crashes
      setColours([]);
      setBySupplier({});
    } finally {
      setIsLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/supplier-colours/suppliers`);
      setSuppliers(res.data?.suppliers || []);
    } catch (error) {
      console.error("Failed to load suppliers", error);
      setSuppliers([]);
    }
  };

  const openAddModal = (supplier = "") => {
    setEditingColour(null);
    setFormData({ supplier: supplier || suppliers[0] || "", name: "", hex: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (colour) => {
    setEditingColour(colour);
    setFormData({
      supplier: colour.supplier,
      name: colour.name,
      hex: colour.hex || "",
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.supplier || !formData.name) {
      toast.error("Supplier and colour name are required");
      return;
    }
    
    setIsSaving(true);
    try {
      if (editingColour) {
        await axios.put(`${API}/supplier-colours/${editingColour.id}`, formData);
        toast.success("Colour updated");
      } else {
        await axios.post(`${API}/supplier-colours`, formData);
        toast.success("Colour created");
      }
      setIsModalOpen(false);
      loadColours();
    } catch (error) {
      console.error("Failed to save colour", error);
      toast.error(error.response?.data?.detail || "Failed to save colour");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (colourId) => {
    if (!window.confirm("Delete this colour?")) return;
    
    try {
      await axios.delete(`${API}/supplier-colours/${colourId}`);
      toast.success("Colour deleted");
      loadColours();
    } catch (error) {
      console.error("Failed to delete colour", error);
      toast.error("Failed to delete colour");
    }
  };

  const handleToggleActive = async (colour) => {
    try {
      await axios.put(`${API}/supplier-colours/${colour.id}`, {
        is_active: !colour.is_active
      });
      toast.success(colour.is_active ? "Colour deactivated" : "Colour activated");
      loadColours();
    } catch (error) {
      console.error("Failed to toggle colour", error);
      toast.error("Failed to toggle colour status");
    }
  };

  const handleImportFromProducts = async () => {
    if (!window.confirm("Import all unique colours from existing products?")) return;
    
    try {
      const res = await axios.post(`${API}/supplier-colours/import-from-products`);
      toast.success(res.data.message);
      loadColours();
      loadSuppliers();
    } catch (error) {
      console.error("Failed to import colours", error);
      toast.error("Failed to import colours");
    }
  };

  const toggleSupplier = (supplier) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplier]: !prev[supplier]
    }));
  };

  const getToken = () => localStorage.getItem("auth_token");

  const handleDownloadTemplate = async (type) => {
    const endpoint = type === "colours" 
      ? "/templates/colour-import" 
      : "/templates/product-colours-import";
    
    try {
      const token = getToken();
      const response = await fetch(`${API}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "colours" ? "colour_library_template.csv" : "product_colours_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      console.error("Failed to download template", error);
      toast.error("Failed to download template");
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error("Please select a file");
      return;
    }

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      
      const token = getToken();
      const endpoint = importType === "colours" 
        ? "/supplier-colours/import-csv" 
        : "/products/import-colours-csv";
      
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      
      const data = response.data;
      if (importType === "colours") {
        toast.success(`Imported: ${data.created} created, ${data.updated} updated${data.total_errors > 0 ? `, ${data.total_errors} errors` : ""}`);
      } else {
        toast.success(`Updated ${data.updated_products} products${data.total_errors > 0 ? `, ${data.total_errors} errors` : ""}`);
      }
      
      if (data.errors?.length > 0) {
        logger.warn("Import errors:", data.errors);
      }
      
      setIsImportModalOpen(false);
      setImportFile(null);
      loadColours();
    } catch (error) {
      console.error("Import failed", error);
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setIsImporting(false);
    }
  };

  // Filter colours by search - with null safety
  const filteredBySupplier = {};
  if (bySupplier && typeof bySupplier === 'object') {
    Object.entries(bySupplier).forEach(([supplier, colourList]) => {
      if (!Array.isArray(colourList)) return;
      const filtered = colourList.filter(c => {
        if (!c) return false;
        const nameMatch = (c.name || '').toLowerCase().includes(search.toLowerCase());
        const hexMatch = (c.hex || '').toLowerCase().includes(search.toLowerCase());
        return nameMatch || hexMatch;
      });
      if (filtered.length > 0) {
        filteredBySupplier[supplier] = filtered;
      }
    });
  }

  // Colour swatch component
  const ColourSwatch = ({ hex, size = "md" }) => {
    const sizeClasses = {
      sm: "w-4 h-4",
      md: "w-6 h-6",
      lg: "w-8 h-8"
    };
    
    return (
      <div 
        className={`${sizeClasses[size]} rounded border border-gray-300 shadow-sm flex-shrink-0`}
        style={{ backgroundColor: hex || "#f3f4f6" }}
        title={hex || "No hex value"}
      >
        {!hex && (
          <div className="w-full h-full flex items-center justify-center text-[8px] text-gray-400">?</div>
        )}
      </div>
    );
  };

  return (
    <WorkspaceLayout
      title="Supplier Colours"
      actions={
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <button
                onClick={() => setIsImportModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-workspace-border text-sm font-medium hover:bg-workspace-hover transition-colors"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </button>
              <button
                onClick={handleImportFromProducts}
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-workspace-border text-sm font-medium hover:bg-workspace-hover transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Sync from Products
              </button>
            </>
          )}
          <button 
            onClick={() => openAddModal()} 
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Colour
          </button>
        </div>
      }
    >
      {/* Search and Filters Bar */}
      <div className="bg-white border-b border-workspace-border px-6 py-3">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-workspace-text-muted" />
            <input
              placeholder="Search colours..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-workspace-bg rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 border border-workspace-border"
            />
          </div>
          
          <Select value={filterSupplier} onValueChange={setFilterSupplier}>
            <SelectTrigger className="w-48 bg-white rounded-md border-workspace-border">
              <Building2 className="w-4 h-4 mr-2 text-workspace-text-muted" />
              <SelectValue placeholder="All Suppliers" />
            </SelectTrigger>
            <SelectContent className="rounded-md bg-white border border-workspace-border">
              <SelectItem value="all">All Suppliers</SelectItem>
              {suppliers.filter(s => s && s.trim() !== '').map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="text-sm text-workspace-text-secondary">
            {colours.length} colours
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        {isLoading ? (
          <div className="text-center py-12 text-workspace-text-muted">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
            <p className="text-sm">Loading colours...</p>
          </div>
        ) : Object.keys(filteredBySupplier).length === 0 ? (
          <div className="text-center py-12 text-workspace-text-muted">
            <Palette className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-medium mb-1 text-workspace-text-primary">No colours found</h3>
            <p className="text-sm mb-4">Add colours or import from existing products</p>
            <Button onClick={handleImportFromProducts} variant="outline">
              <Upload className="w-4 h-4 mr-2" />
              Import from Products
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(filteredBySupplier).sort().map(([supplier, colourList]) => {
              // Ensure colourList is always an array
              const safeColourList = Array.isArray(colourList) ? colourList : [];
              const missingHexCount = safeColourList.filter(c => c && !c.hex).length;
              
              return (
              <Collapsible
                key={supplier || 'unknown'}
                open={expandedSuppliers[supplier]}
                onOpenChange={() => toggleSupplier(supplier)}
              >
                <div className="bg-white rounded-lg border border-workspace-border shadow-sm overflow-hidden">
                  <div className="w-full px-4 py-3 flex items-center justify-between hover:bg-workspace-hover">
                    <CollapsibleTrigger className="flex items-center gap-3 flex-1">
                      <Building2 className="w-5 h-5 text-orange-500" />
                      <span className="font-medium text-workspace-text-primary">{supplier || 'Unknown'}</span>
                      <Badge variant="secondary" className="text-xs">
                        {safeColourList.length} colours
                      </Badge>
                      {missingHexCount > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {missingHexCount} missing hex
                        </Badge>
                      )}
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openAddModal(supplier); }}
                        className="h-7"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                      <CollapsibleTrigger className="p-1">
                        {expandedSuppliers[supplier] ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </CollapsibleTrigger>
                    </div>
                  </div>
                  
                  <CollapsibleContent>
                    <div className="border-t border-workspace-border p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {safeColourList.map((colour) => {
                          if (!colour || !colour.id) return null;
                          return (
                          <div
                            key={colour.id}
                            className={`bg-workspace-bg rounded-lg p-3 flex flex-col items-center gap-2 hover:shadow-md transition-shadow group relative ${
                              colour.is_active === false ? "opacity-50" : ""
                            } ${!colour.hex ? "ring-2 ring-red-300 ring-offset-1" : ""}`}
                          >
                            {colour.is_active === false && (
                              <span className="absolute top-1 right-1 text-[9px] bg-gray-300 text-gray-600 px-1 rounded">
                                Inactive
                              </span>
                            )}
                            {!colour.hex && (
                              <span className="absolute top-1 left-1 text-[9px] bg-red-100 text-red-600 px-1 rounded flex items-center gap-0.5">
                                <AlertCircle className="w-2.5 h-2.5" />
                                No hex
                              </span>
                            )}
                            <ColourSwatch hex={colour.hex} size="lg" />
                            <span className="text-xs font-medium text-center text-workspace-text-primary line-clamp-2">
                              {colour.name || "Unnamed"}
                            </span>
                            {colour.hex ? (
                              <span className="text-[10px] font-mono text-workspace-text-muted">
                                {colour.hex}
                              </span>
                            ) : (
                              <span className="text-[10px] font-mono text-red-400">
                                Click to add hex
                              </span>
                            )}
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleToggleActive(colour)}
                                className={`p-1 ${colour.is_active === false ? "text-green-500 hover:text-green-600" : "text-workspace-text-muted hover:text-amber-500"}`}
                                title={colour.is_active === false ? "Activate" : "Deactivate"}
                              >
                                {colour.is_active === false ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                              </button>
                              <button
                                onClick={() => openEditModal(colour)}
                                className="p-1 text-workspace-text-muted hover:text-orange-500"
                              >
                                <Edit className="w-3 h-3" />
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDelete(colour.id)}
                                  className="p-1 text-workspace-text-muted hover:text-red-500"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        );})}
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );})}
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingColour ? "Edit Colour" : "Add Colour"}
            </DialogTitle>
            <DialogDescription>
              Define a colour for the supplier library
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Select
                value={formData.supplier}
                onValueChange={(v) => setFormData({ ...formData, supplier: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.filter(s => s && s.trim() !== '').map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                  <SelectItem value="__new">+ Add New Supplier</SelectItem>
                </SelectContent>
              </Select>
              {formData.supplier === "__new" && (
                <Input
                  placeholder="Enter new supplier name..."
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  className="mt-2"
                />
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Colour Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Navy, Bone, Grey Marle"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Hex Value</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.hex}
                  onChange={(e) => setFormData({ ...formData, hex: e.target.value })}
                  placeholder="#000000"
                  className="flex-1 font-mono"
                />
                <input
                  type="color"
                  value={formData.hex || "#000000"}
                  onChange={(e) => setFormData({ ...formData, hex: e.target.value.toUpperCase() })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
              </div>
              
              {/* Quick colour presets */}
              <div className="flex flex-wrap gap-1 mt-2">
                {Object.entries(COMMON_COLOURS).map(([name, hex]) => (
                  <button
                    key={name}
                    onClick={() => setFormData({ ...formData, name: formData.name || name, hex })}
                    className="flex items-center gap-1 px-2 py-0.5 text-[10px] bg-orange-50 text-orange-700 rounded hover:bg-orange-100"
                    title={`${name}: ${hex}`}
                  >
                    <div 
                      className="w-3 h-3 rounded border border-gray-300"
                      style={{ backgroundColor: hex }}
                    />
                    {name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Preview */}
            <div className="p-4 bg-workspace-bg rounded-lg flex items-center gap-4">
              <ColourSwatch hex={formData.hex} size="lg" />
              <div>
                <p className="font-medium text-sm text-workspace-text-primary">{formData.name || "Colour Name"}</p>
                <p className="text-xs text-workspace-text-muted">{formData.supplier || "Supplier"}</p>
                <p className="text-xs font-mono text-workspace-text-muted">{formData.hex || "No hex"}</p>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : editingColour ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Colours from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import colours or map colours to products.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Import Type Selection */}
            <div className="space-y-2">
              <Label>Import Type</Label>
              <Select value={importType} onValueChange={setImportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="colours">Colour Library (add/update colours)</SelectItem>
                  <SelectItem value="product-colours">Product Colours (map colours to products)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Template Download */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Step 1:</strong> Download the template to see the required format
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleDownloadTemplate(importType)}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                <Download className="w-4 h-4 mr-2" />
                Download {importType === "colours" ? "Colour Library" : "Product Colours"} Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Upload CSV File</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  {importFile ? (
                    <p className="text-sm font-medium text-green-600">{importFile.name}</p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">Click to select CSV file</p>
                      <p className="text-xs text-gray-400 mt-1">or drag and drop</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Format Info */}
            <div className="text-xs text-gray-500 space-y-1">
              {importType === "colours" ? (
                <>
                  <p><strong>Expected columns:</strong> supplier, name, hex, is_active (optional)</p>
                  <p><strong>Example:</strong> AS Colour,Black,#000000,true</p>
                </>
              ) : (
                <>
                  <p><strong>Expected columns:</strong> product_name, supplier, colours</p>
                  <p><strong>Example:</strong> Staple Tee,AS Colour,"Black,White,Navy"</p>
                </>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setIsImportModalOpen(false);
              setImportFile(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleImportCSV} disabled={isImporting || !importFile}>
              {isImporting ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WorkspaceLayout>
  );
}
